import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, TextField } from '../components/ui.jsx';
import { ArrowRight, Check } from '../components/icons.jsx';
import { UK_PHONE_HINT, ukPhoneError } from '../lib/phone.js';

/**
 * The results screen: tariff cards (home only) followed by the details form.
 *
 * This is where the lead is actually captured, so the contact fields moved off
 * their own step and onto this one — the customer fills them in with the offer
 * they picked still on screen.
 */
const CONSENT_TEXT =
  'By proceeding, you agree to receive communications from the service provider through phone, ' +
  'email, and SMS. Rest assured that we will only share the information you provided with your ' +
  'selected provider exclusively for re-marketing purposes.';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Client-side validation mirrors the server's, purely to save a round trip. */
function validate(values) {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = 'Please enter your first name';
  if (!values.lastName.trim()) errors.lastName = 'Please enter your last name';
  if (!values.email.trim()) errors.email = 'Please enter your email address';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Please enter a valid email address';

  // UK-only funnel: anything we cannot dial is rejected here rather than being
  // stamped +44 and handed to sales as an unreachable number.
  const phoneError = ukPhoneError(values.phone);
  if (phoneError) errors.phone = phoneError;

  if (!values.consentTerms) errors.consentTerms = 'Please accept the terms to continue';
  return errors;
}

function OfferCard({ offer, selected, onChoose }) {
  return (
    <article className="offer" data-tone={offer.tone} data-selected={selected ? 'true' : undefined}>
      <p className="offer__ribbon">{offer.ribbon}</p>
      <div className="offer__body">
        <h3 className="offer__name">{offer.name}</h3>
        <img className="offer__image" src={offer.image} alt="" aria-hidden="true" loading="lazy" />

        <p className="offer__price">
          <span className="offer__pounds">£{offer.price.pounds}</span>
          <span className="offer__pence">.{offer.price.pence}</span>
          <span className="offer__period">{offer.period}</span>
        </p>

        <ul className="offer__features">
          {offer.features.map((feature) => (
            <li key={`${feature.strong ?? ''}${feature.rest}`}>
              <span className="offer__tick" aria-hidden="true">
                <Check size={12} />
              </span>
              <span>
                {feature.strong && <strong>{feature.strong}</strong>}
                {feature.rest}
              </span>
            </li>
          ))}
        </ul>

        <button type="button" className="offer__choose" onClick={() => onChoose(offer)}>
          <span>{selected ? 'Selected' : 'Choose Offer'}</span>
          <ArrowRight size={16} />
        </button>
      </div>
    </article>
  );
}

export default function OffersStep({
  offers,
  formTitle,
  selectedOffer,
  onChooseOffer,
  onSubmit,
  submitting,
  submitError,
}) {
  // "See more offers" is a navigation aid, not an answer: per the designer it
  // simply carries the customer down to the details form. Recording it as a
  // chosen offer would put a fake tariff on the lead.
  const [scrollNonce, setScrollNonce] = useState(0);
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    // The Figma shows this pre-ticked. It ships unticked: under UK GDPR and
    // PECR marketing consent has to be an affirmative act, and a pre-ticked
    // box is not one. Flip the default here if the business accepts that risk.
    consentTerms: false,
    // Honeypot. Hidden from real users; bots fill it in.
    website: '',
  });
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const set = (field) => (value) => {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  // Picking an offer should carry the customer to the thing they now have to
  // do, which is several hundred pixels further down on desktop.
  useEffect(() => {
    if (!selectedOffer && !scrollNonce) return;
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedOffer, scrollNonce]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);

    if (Object.keys(found).length) {
      // Send focus to the first problem rather than making the customer hunt.
      const firstField = Object.keys(found)[0];
      document.querySelector(`[name="${firstField}"]`)?.focus();
      return;
    }

    onSubmit({
      ...values,
      // One box in the design covers both the marketing permission and the
      // terms, so it satisfies both flags the API records.
      consentMarketing: true,
      consentText: CONSENT_TEXT,
    });
  };

  return (
    <div className="offers-step">
      {offers.length > 0 && (
        <>
          <div className="offers">
            {offers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                selected={selectedOffer?.id === offer.id}
                onChoose={onChooseOffer}
              />
            ))}
          </div>

          <div className="offers__more">
            <p>Can’t see an offer that suits you?</p>
            <button
              type="button"
              className="offers__more-button"
              onClick={() => setScrollNonce((n) => n + 1)}
            >
              <span>I’d like to see more offers</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </>
      )}

      <div ref={formRef}>
        <Card className="card--form">
          <form onSubmit={handleSubmit} noValidate>
            <h2 className="card__title">{formTitle}</h2>

            {submitError && <Alert>{submitError}</Alert>}

            <TextField
              label="First name *"
              name="firstName"
              placeholder="Type here..."
              value={values.firstName}
              onChange={(e) => set('firstName')(e.target.value)}
              error={errors.firstName}
              autoComplete="given-name"
              required
            />

            <TextField
              label="Last name *"
              name="lastName"
              placeholder="Type here..."
              value={values.lastName}
              onChange={(e) => set('lastName')(e.target.value)}
              error={errors.lastName}
              autoComplete="family-name"
              required
            />

            <TextField
              label="Contact Number *"
              name="phone"
              type="tel"
              placeholder="07700 900123"
              hint={UK_PHONE_HINT}
              value={values.phone}
              onChange={(e) => set('phone')(e.target.value)}
              error={errors.phone}
              autoComplete="tel"
              inputMode="tel"
              required
            />

            <TextField
              label="Contact Email *"
              name="email"
              type="email"
              placeholder="Type here..."
              value={values.email}
              onChange={(e) => set('email')(e.target.value)}
              error={errors.email}
              autoComplete="email"
              inputMode="email"
              required
            />

            {/* Honeypot — off-screen rather than display:none, which some bots skip. */}
            <div className="visually-hidden" aria-hidden="true">
              <label htmlFor="website">Leave this field empty</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={values.website}
                onChange={(e) => set('website')(e.target.value)}
              />
            </div>

            <Checkbox
              name="consentTerms"
              label={CONSENT_TEXT}
              checked={values.consentTerms}
              onChange={set('consentTerms')}
              error={errors.consentTerms}
            />

            <Button type="submit" loading={submitting}>
              Continue
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
